import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLock, FiCpu, FiSave, FiCheck, FiAlertCircle, FiX, FiSettings, FiCamera, FiLogOut, FiChevronDown } = FiIcons;

const UserMenu = () => {
  const { user, updateUserProfile, uploadUserAvatar, changePassword, signOut } = useAuth();
  const { success: showSuccess, error: showError } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const {
    state: logoutState,
    handleAction: handleLogoutAction,
    icon: logoutIcon,
    text: logoutText,
    classes: logoutClasses,
  } = useButtonState({
    idleText: 'Sign Out',
    loadingText: 'Signing out...',
    successText: 'Signed out!',
    failedText: 'Failed',
    idleIcon: FiLogOut,
    onSuccess: () => {
      showSuccess('Logged out successfully');
      setIsOpen(false);
    },
    autoResetDelay: 2000,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    handleLogoutAction(async () => {
      await signOut();
    });
  };

  const handleOpenSettingsLocal = () => {
    setIsOpen(false);
    window.dispatchEvent(new CustomEvent('openSettings'));
  };

  const getInitials = (user) => {
      const name = user.display_name || user.user_metadata?.name || user.email.split('@')[0];
      return name[0].toUpperCase();
  };

  const getDisplayName = (user) => {
      return user.display_name || user.user_metadata?.name || user.email.split('@')[0];
  };

  const getAvatarUrl = (user) => {
      if (user.profile_image) {
          return user.profile_image;
      }
      if (user.avatar_url) {
          if (user.avatar_url.startsWith('http')) {
              return user.avatar_url;
          }
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
          return `${apiUrl}${user.avatar_url}`;
      }
      return null;
  };

  const avatarUrl = getAvatarUrl(user);
  const displayName = getDisplayName(user);
  const initials = getInitials(user);

  return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center space-x-2 border-2 border-black px-3 py-1.5 transition-all ${
                isOpen ? 'bg-black text-white' : 'bg-white hover:bg-gray-100 shadow-neo'
                }`}
            >
                <div className="bg-neo-purple border border-black rounded-full p-1 min-w-[44px] min-h-[44px] w-11 h-11 flex items-center justify-center overflow-hidden">
                    {avatarUrl ? (
                         <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover rounded-full" />
                    ) : (
                         <SafeIcon icon={FiUser} size={16} className={isOpen ? "text-white" : "text-black"} />
                    )}
                </div>
                <span className="font-bold text-sm hidden sm:inline max-w-[100px] truncate">
                {displayName}
                </span>
                <SafeIcon icon={FiChevronDown} size={14} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-64 max-w-[90vw] bg-white border-2 border-black shadow-neo-lg z-50 animate-in fade-in zoom-in-95 duration-100 overflow-hidden">
                 <div className="bg-neo-yellow border-b-2 border-black p-4">
                    <div className="flex items-center space-x-3">
                        <div className="h-11 w-11 min-w-[44px] min-h-[44px] bg-black text-white flex items-center justify-center border-2 border-white shadow-sm overflow-hidden rounded-full">
                             {avatarUrl ? (
                                 <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                             ) : (
                                <span className="font-black text-lg">
                                    {initials}
                                </span>
                             )}
                        </div>
                        <div className="overflow-hidden">
                            <p className="text-sm font-black truncate text-black">
                                {displayName}
                            </p>
                            <p className="text-xs font-mono truncate text-gray-700">
                                {user.email}
                            </p>
                        </div>
                    </div>
                </div>
                 <div className="p-2 space-y-1">
                      <button
                          onClick={handleOpenSettingsLocal}
                          className="flex items-center space-x-2 px-3 py-2 min-h-[44px] hover:bg-gray-100 border border-transparent hover:border-black transition-all w-full text-left"
                      >
                          <SafeIcon icon={FiSettings} size={16} />
                          <span className="text-sm font-bold">Settings</span>
                      </button>
                       <button
                           onClick={handleLogout}
                           disabled={logoutState === 'loading'}
                           className={`flex items-center space-x-2 px-3 py-2 min-h-[44px] border border-transparent hover:border-black transition-all w-full text-left ${logoutState === 'loading' ? 'opacity-80 cursor-wait' : 'hover:bg-red-100 text-red-600'} ${logoutClasses}`}
                       >
                           <SafeIcon icon={logoutState === 'loading' ? FiLogOut : logoutIcon} size={16} className={logoutState === 'loading' ? 'animate-spin' : ''} />
                           <span className="text-sm font-bold">{logoutText}</span>
                       </button>
                 </div>
                </div>
            )}
        </div>
  );
};

export default UserMenu;